import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getClientId } from "./clientId";
import { useAuth } from "../context/AuthContext";
import { fetchTrustedCreators, addTrustedCreator, delTrustedCreator } from "./api";

export const useTrustedCreators = () => {
  const clientId = getClientId();
  const qc = useQueryClient();
  const { requireAuth } = useAuth();
  const { data } = useQuery({
    queryKey: ["trustedCreators", clientId],
    queryFn: () => fetchTrustedCreators(clientId),
    staleTime: 60000,
  });

  const creators = data?.creators || []; // [{ creator, label }]
  const trustedSet = new Set(creators.map((c) => c.creator));

  // group wallets under a dev name
  const grouped = {};
  creators.forEach((c) => {
    const n = (c.label || "").trim() || "Unnamed dev";
    (grouped[n] = grouped[n] || []).push(c.creator);
  });
  const devList = Object.entries(grouped)
    .map(([name, addresses]) => ({ name, addresses }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const names = devList.map((d) => d.name);

  const refresh = () => qc.invalidateQueries({ queryKey: ["trustedCreators", clientId] });

  const addWallet = async (name, address) => {
    if (!requireAuth("track Alpha devs")) return;
    const res = await addTrustedCreator(clientId, { creator: address, name });
    refresh();
    const n = res?.walletsAdded || 1;
    if (n > 1) {
      toast.success(`${name} → all ${n} known wallets added to Alpha — you'll be pinged on any of their launches`);
    } else {
      toast.success(`Wallet added to ${name} — you'll be pinged on their launches`);
    }
    return res;
  };
  const removeWallet = async (address) => {
    await delTrustedCreator(clientId, address);
    refresh();
    toast.success("Wallet removed");
  };

  const isAlpha = (creator) => !!creator && trustedSet.has(creator);
  const nameFor = (creator) => (creators.find((c) => c.creator === creator)?.label || "");

  return { creators, devList, names, trustedSet, isAlpha, nameFor, addWallet, removeWallet };
};
